"use client";

import Image from "next/image";
import Link from "next/link";
import { Phone, ArrowRight, Shield, ExternalLink, X, Check } from "lucide-react";
import { BRAND } from "@/lib/brand";

const F = { display: "'Plus Jakarta Sans', sans-serif", body: '"Barlow", sans-serif', mono: '"DM Mono", monospace' };
const C = { gold: "#D4A84B", white: "#ffffff", w82: "rgba(255,255,255,0.82)", w55: "rgba(255,255,255,0.70)", w30: "rgba(255,255,255,0.30)", navy: "#080F1A", navyMid: "#0d1929" };
const CARD: React.CSSProperties = { background: "rgba(255,255,255,0.10)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)", border: "1px solid rgba(255,255,255,0.15)", borderTop: `2px solid ${C.gold}`, borderRadius: 14, padding: "2rem", display: "flex", flexDirection: "column" };
const PANEL: React.CSSProperties = { background: "rgba(255,255,255,0.12)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.18)", borderRadius: 16, padding: "2.5rem" };
const LABEL: React.CSSProperties = { color: C.gold, fontFamily: F.mono, fontSize: 10, letterSpacing: 3, textTransform: "uppercase", display: "block", marginBottom: "0.6rem" };
const H2: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "clamp(1.8rem, 3.5vw, 3rem)", marginBottom: "0.75rem", lineHeight: 1.1 };
const H3: React.CSSProperties = { color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.3rem", marginBottom: "0.75rem" };
const P: React.CSSProperties = { color: C.w82, fontSize: "0.95rem", lineHeight: 1.75, fontFamily: F.body };
const OVERLAY = (o: number): React.CSSProperties => ({ position: "absolute", inset: 0, background: `rgba(5,13,24,${o})`, zIndex: 1 });
const CONTENT: React.CSSProperties = { position: "relative", zIndex: 2, padding: "5rem 1.5rem", maxWidth: 1200, margin: "0 auto", width: "100%" };
const SEC: React.CSSProperties = { position: "relative", overflow: "hidden", backgroundColor: C.navy };
const GOLDLINE: React.CSSProperties = { width: 48, height: 2, background: C.gold, borderRadius: 2, margin: "16px 0" };
const BTN_RED: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 10, background: "linear-gradient(135deg, #DC2626 0%, #dc2626 100%)", color: C.white, fontFamily: F.display, fontWeight: 700, fontSize: "1.1rem", borderRadius: 12, padding: "16px 36px", textDecoration: "none", border: "none", letterSpacing: "1px", boxShadow: "0 8px 32px rgba(220,38,38,0.55), 0 2px 8px rgba(220,38,38,0.4)" };
const BTN_GOLD: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", color: C.gold, fontFamily: F.body, fontWeight: 600, fontSize: "1rem", border: `1.5px solid ${C.gold}`, borderRadius: 10, padding: "13px 26px", textDecoration: "none" };

const commitments = [
  { title: "You&rsquo;ll know what to expect before we hang up", body: "Before we hang up, you&rsquo;ll know what to expect — we give you a clear timeframe so you&rsquo;re never left waiting and wondering." },
  { title: "Our people, not strangers", body: "Our team members are DRR employees — trained, certified, and invested in doing the job right from day one to the last day." },
  { title: "We speak to your insurer", body: "Early, clear communication with your insurance carrier helps expedite claim review and approval. We speak their language so you don&rsquo;t have to." },
  { title: "One PM. One number. Every day.", body: "One project manager. One number. Every day you know exactly where your project stands." },
  { title: "Respect for your property", body: "We treat your building like it&rsquo;s our building. Documented before and after. Nothing moved without your knowledge." },
  { title: "Documentation that protects you", body: "Our documentation wins insurance claims and prevents lawsuits. IICRC logs, moisture readings, Xactimate scopes — every job, every time." },
  { title: "Fully verifiable", body: "AZ ROC #349012 — licensed, bonded, insured. Verify at roc.az.gov anytime." },
  { title: "Start to finish", body: "From day one to the day the project is completed. Same team. No handoffs. No surprises." },
];

const contrasts = [
  { problem: "24-hour wait", answer: "Clear timeframe before we hang up" },
  { problem: "Bills run 80%+ over estimate", answer: "Written Xactimate L3 scope upfront" },
  { problem: "Unknown subcontractors", answer: "DRR employees only" },
  { problem: "Zero communication", answer: "One PM, daily updates" },
  { problem: "Property damage", answer: "Before/after documentation, nothing moved without permission" },
  { problem: "Jobs abandoned", answer: "IICRC-compliant scope, committed timeline" },
  { problem: "Unlicensed storm-chasers", answer: "AZ ROC #349012 — verify at roc.az.gov" },
  { problem: "No ICRA, no OSHA", answer: "Same enterprise-grade process for every property type" },
];

export default function OurCommitmentsPage() {
  return (
    <>
      {/* ═══ HERO ═══ */}
      <section style={{ ...SEC, minHeight: "80vh", display: "flex", alignItems: "center" }}>
        <Image src="/images/arizona-landmarks/havasupai-falls-arizona.jpg" alt="Arizona architecture" fill style={{ objectFit: "cover" }} priority />
        <div style={OVERLAY(0.60)} />
        <div style={{ ...CONTENT, maxWidth: 900, textAlign: "center" }}>
          <span style={LABEL}>Our Commitments</span>
          <h1 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(2.2rem, 5vw, 4.2rem)", lineHeight: 1.05, marginBottom: 20 }}>
            Eight Commitments.<br />Every Job. No Exceptions.
          </h1>
          <div style={{ ...GOLDLINE, margin: "20px auto" }} />
          <p style={{ ...P, fontSize: "1.1rem", maxWidth: 680, margin: "0 auto" }}>
            We don&rsquo;t ask you to trust us. We give you 8 specific commitments so you know exactly what to expect.
          </p>
        </div>
      </section>

      {/* ═══ THE 8 COMMITMENTS ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={CONTENT}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span style={LABEL}>Our Promise</span>
            <h2 style={H2}>What We Commit To — Every Time</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.5rem" }}>
            {commitments.map((c, i) => (
              <div key={i} style={{ ...CARD, flexDirection: "row", gap: 20, padding: "1.75rem" }}>
                <div style={{ flexShrink: 0 }}>
                  <span style={{ color: C.gold, fontFamily: F.display, fontWeight: 800, fontSize: "3rem", lineHeight: 1, display: "block" }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ ...H3, fontSize: "1.15rem", marginBottom: 10 }} dangerouslySetInnerHTML={{ __html: c.title }} />
                  <p style={{ ...P, fontSize: "0.9rem" }} dangerouslySetInnerHTML={{ __html: c.body }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ VERIFICATION CALLOUT ═══ */}
      <section style={{ ...SEC, padding: "60px 24px" }}>
        <div style={{ maxWidth: 850, margin: "0 auto" }}>
          <div style={{ ...PANEL, padding: "2.5rem" }}>
            <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
              <Shield size={32} style={{ color: C.gold, flexShrink: 0, marginTop: 4 }} />
              <div style={{ flex: 1, minWidth: 260 }}>
                <span style={{ ...LABEL, marginBottom: 10 }}>Publicly Verifiable</span>
                <h3 style={{ ...H3, fontSize: "1.25rem" }}>Every Credential DRR Holds Is Publicly Verifiable</h3>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "0.75rem", marginTop: 16 }}>
                  <a href="https://roc.az.gov" target="_blank" rel="noopener noreferrer" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: "0.9rem 1.1rem", textDecoration: "none", display: "block" }}>
                    <span style={{ fontFamily: F.mono, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: C.gold, display: "block", marginBottom: 4 }}>AZ ROC License</span>
                    <span style={{ color: C.white, fontWeight: 700, fontSize: "0.95rem", display: "inline-flex", alignItems: "center", gap: 6 }}>#349012 <ExternalLink size={12} /></span>
                  </a>
                  <a href="https://www.bbb.org/us/az/tempe/profile/fire-water-damage-restoration/disaster-recovery-restoration-llc-1126-1000108487" target="_blank" rel="noopener noreferrer" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: "0.9rem 1.1rem", textDecoration: "none", display: "block" }}>
                    <span style={{ fontFamily: F.mono, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: C.gold, display: "block", marginBottom: 4 }}>BBB Rating</span>
                    <span style={{ color: C.white, fontWeight: 700, fontSize: "0.95rem", display: "inline-flex", alignItems: "center", gap: 6 }}>A+ Accredited <ExternalLink size={12} /></span>
                  </a>
                  <a href="https://sam.gov" target="_blank" rel="noopener noreferrer" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: "0.9rem 1.1rem", textDecoration: "none", display: "block" }}>
                    <span style={{ fontFamily: F.mono, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: C.gold, display: "block", marginBottom: 4 }}>SAM.gov UEI</span>
                    <span style={{ color: C.white, fontWeight: 700, fontSize: "0.95rem", display: "inline-flex", alignItems: "center", gap: 6 }}>JC2UG98VRSA9 <ExternalLink size={12} /></span>
                  </a>
                  <div style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)", borderRadius: 10, padding: "0.9rem 1.1rem" }}>
                    <span style={{ fontFamily: F.mono, fontSize: "9px", letterSpacing: "2px", textTransform: "uppercase", color: C.gold, display: "block", marginBottom: 4 }}>D-U-N-S</span>
                    <span style={{ color: C.white, fontWeight: 700, fontSize: "0.95rem" }}>108514521</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ COMPETITOR CONTRAST ═══ */}
      <section style={{ ...SEC, backgroundColor: C.navyMid }}>
        <div style={CONTENT}>
          <div style={{ textAlign: "center", marginBottom: "3rem" }}>
            <span style={LABEL}>The Difference</span>
            <h2 style={H2}>What You&rsquo;ve Probably Experienced Before</h2>
            <div style={{ ...GOLDLINE, margin: "16px auto" }} />
          </div>
          <div style={{ ...PANEL, padding: "0", overflow: "hidden" }}>
            {/* Header row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, background: "rgba(0,0,0,0.25)", borderBottom: "1px solid rgba(255,255,255,0.10)" }}>
              <div style={{ padding: "1rem 1.5rem", borderRight: "1px solid rgba(255,255,255,0.10)" }}>
                <span style={{ color: "rgba(220,38,38,0.9)", fontFamily: F.mono, fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", fontWeight: 600 }}>The Problem</span>
              </div>
              <div style={{ padding: "1rem 1.5rem" }}>
                <span style={{ color: C.gold, fontFamily: F.mono, fontSize: "10px", letterSpacing: "2px", textTransform: "uppercase", fontWeight: 600 }}>What We Commit To</span>
              </div>
            </div>
            {/* Rows */}
            {contrasts.map((c, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 0, borderBottom: i < contrasts.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none" }}>
                <div style={{ padding: "1.25rem 1.5rem", borderRight: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "flex-start", gap: 12 }}>
                  <X size={16} style={{ color: "rgba(220,38,38,0.7)", flexShrink: 0, marginTop: 3 }} />
                  <span style={{ ...P, fontSize: "0.9rem", color: C.w55 }}>{c.problem}</span>
                </div>
                <div style={{ padding: "1.25rem 1.5rem", display: "flex", alignItems: "flex-start", gap: 12, background: "rgba(212,168,75,0.04)" }}>
                  <Check size={16} style={{ color: C.gold, flexShrink: 0, marginTop: 3 }} />
                  <span style={{ ...P, fontSize: "0.9rem", color: C.white, fontWeight: 500 }}>{c.answer}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ WHO WE HELP LINKS ═══ */}
      <section style={{ ...SEC, padding: "60px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <span style={LABEL}>Who We Work With</span>
          <h3 style={{ ...H3, fontSize: "1.4rem", marginBottom: 24 }}>Same Commitments. Every Property Type.</h3>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
            <Link href="/who-we-help/commercial" style={{ ...BTN_GOLD, fontSize: "0.9rem", padding: "10px 20px" }}>
              Commercial Property <ArrowRight size={14} />
            </Link>
            <Link href="/who-we-help/healthcare" style={{ ...BTN_GOLD, fontSize: "0.9rem", padding: "10px 20px" }}>
              Healthcare Facilities <ArrowRight size={14} />
            </Link>
            <Link href="/who-we-help/residential" style={{ ...BTN_GOLD, fontSize: "0.9rem", padding: "10px 20px" }}>
              Residential <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* ═══ CTA ═══ */}
      <section style={{ ...SEC, minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Image src="/images/arizona-landmarks/monument-valley-night-arizona.jpg" alt="Arizona night" fill style={{ objectFit: "cover" }} />
        <div style={OVERLAY(0.55)} />
        <div style={{ ...CONTENT, maxWidth: 750, textAlign: "center" }}>
          <div style={{ ...PANEL, padding: "56px 40px" }}>
            <span style={{ ...LABEL, marginBottom: 20 }}>AVAILABLE 24 · 7 · 365</span>
            <h2 style={{ color: C.white, fontFamily: F.display, fontWeight: 800, fontSize: "clamp(1.8rem, 4vw, 3rem)", lineHeight: 1.08, marginBottom: 16 }}>
              Eight commitments.<br />One call.
            </h2>
            <p style={{ ...P, fontSize: "1rem", marginBottom: 36, maxWidth: 520, margin: "0 auto 36px" }}>
              From day one to the day the project is completed. Publicly verifiable. Privately accountable.
            </p>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
              <a href={BRAND.phoneHref} data-cta="emergency" style={BTN_RED}>
                <Phone size={20} /> {BRAND.phone}
              </a>
              <Link href="/contact" style={BTN_GOLD}>
                Request Vendor Consultation <ArrowRight size={16} />
              </Link>
            </div>
            <p style={{ marginTop: 28, fontFamily: F.mono, fontSize: "0.62rem", letterSpacing: "2px", textTransform: "uppercase", color: C.w30 }}>
              AZ ROC #349012 · Verify at roc.az.gov
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
